import { useMemo } from "react"
import { Card } from "react-bootstrap"
import {
  NO_VALUE,
  count,
  money,
  safeDivide,
  windowTotals,
} from "../../../performanceSnapshot"

/*
    Conversions tab KPI cards (MPN-1530 §3.1).

    Fixed order, outcome first: Total Conversions, CPC, CPM, Engagements, Total Clicks,
    Total Impressions. It used to run the other way, opening on Total Impressions -- the
    number that says least about whether the campaign worked -- and closing on
    conversions.

    One list rather than six copied Card blocks. The previous version repeated the same
    twelve lines of markup per metric, which is why adding CPC and CPM to it would have
    meant two more copies.

    Reads its numbers from windowTotals so these cards and the Performance Snapshot
    above cannot disagree about the same window's CPC.
*/

const ConversionsHud = ({ data }: { data: any }) => {
  const cards = useMemo(() => {
    const { spend, clicks, impressions, engagements, conversions } = windowTotals(data)

    // Blended over the whole window: SUM(spend) / SUM(clicks), never a mean of daily
    // rates. A dash where the denominator is zero, per §3.3, rather than $0.00 --
    // "no clicks yet" is not the same claim as "clicks were free".
    const cpc = safeDivide(spend, clicks)
    const cpm = safeDivide(spend * 1000, impressions)

    return [
      { key: "conversions", label: "Total Conversions", value: count(conversions) },
      { key: "cpc", label: "CPC", value: cpc === null ? NO_VALUE : money(cpc) },
      { key: "cpm", label: "CPM", value: cpm === null ? NO_VALUE : money(cpm) },
      { key: "engagements", label: "Engagements", value: count(engagements) },
      { key: "clicks", label: "Total Clicks", value: count(clicks) },
      { key: "impressions", label: "Total Impressions", value: count(impressions) },
    ]
  }, [data])

  return (
    <>
      {cards.map((card) => (
        <Card
          key={card.key}
          className="custom-card mb-0 border border-2 border-solid align-items-center"
          style={{ minWidth: 124, width: "fit-content" }}
        >
          <Card.Body className="py-2">
            <div className="d-flex align-items-top">
              <div className="flex-fill">
                <div className="d-flex mb-1 align-items-top justify-content-center">
                  <h5 className="fw-semibold mb-0 lh-1">{card.value}</h5>
                </div>
                <p className="mb-0 fs-10 op-7 text-muted fw-semibold text-center">
                  {card.label}
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
      ))}
    </>
  )
}

export default ConversionsHud
